import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AnnotationCssService {
  translate = inject(TranslateService);

  locals: Record<string, string> = {
    'context.highlight.yellow': 'Yellow',
    'context.highlight.blue': 'Blue',
    'context.highlight.green': 'Green',
    'context.highlight.pink': 'Pink',
    'annotation.note': 'Note',
    'annotation.placemark': 'Placemark',
    'annotation.epr-shape-square-filled': 'Highlight',
    'annotation.epr-shape-square-outline': 'Box',
    'annotation.epr-shape-round-outline': 'Circle',
    'annotation.epr-shape-underline': 'Underline',
    'shared.with': 'with',
    'shared.and': 'and',
    'annotation.begin': 'Started',
    'annotation.end': 'Ended',
    'teaching_notes.answer': 'Answer',
    'teaching_notes.diff_resource': 'Differentiated Resource',
    'teaching_notes.example': 'Example',
    'teaching_notes.teach_notes': 'Teaching Notes',
  };

  get data(): { teacherCss: string, annotationCss: string } {
    return {
      teacherCss: this.getTeacherCss(),
      annotationCss: this.getAnnotationCss(),
    };
  }

  translateLocals(): void {
    for (const key of Object.keys(this.locals)) {
      const translated = this.translate.instant(key);
      if (translated !== key) {
        this.locals[key] = translated;
      }
    }
  }

  getAnnotationCss(): string {
    this.translateLocals();
    const annotationClassNames: Record<string, string[]> = {
      positionName: [
        'annotation-start',
        'annotation-end',
        'annotation-start-end',
      ],
      highlight: [
        'yellow-highlight',
        'blue-highlight',
        'green-highlight',
        'pink-highlight',
      ],
      shape: [
        'epr-shape-underline',
        'epr-shape-round-outline',
        'epr-shape-square-outline',
        'epr-shape-square-filled',
      ],
      note: ['note'],
      placemark: ['placemark'],
    };
    const [beforeCssContent, afterCssContent] =
      this.getAnnotationBeforeCssContent(annotationClassNames);
    const basicSelectors =
      this.getAnnotationBasicSelectors(annotationClassNames);
    const beforeCssFont = this.getAnnotationBeforeCssFont(basicSelectors);
    const afterCss = this.getAnnotationAfterCss(basicSelectors);
    const annotationCss = [
      beforeCssContent,
      beforeCssFont,
      afterCss,
      afterCssContent,
    ].join('');

    return annotationCss;
  }

  getAnnotationBasicSelectors(
    annotationClassNames,
    additionalClass = '',
  ): string[] {
    const selectors: string[] = [];
    const allClassNames: any[] = [].concat(
      annotationClassNames.positionName,
      annotationClassNames.highlight,
      annotationClassNames.note,
      annotationClassNames.placemark,
      annotationClassNames.shape,
    );

    // angular.forEach(allClassNames, function(className) {
    allClassNames.forEach((className) => {
      if (additionalClass) {
        className += '.' + additionalClass;
      }
      if (className.indexOf('highlight') > -1) {
        selectors.push('.annotation.highlight.' + className);
      } else {
        selectors.push('.annotation.' + className);
      }
    });

    return selectors;
  }

  getAnnotationBeforeCssContent(annotationClassNames: Record<string, string[]>): string[] {
    let beforeCssContent = '';
    let afterCssContent = '';

    const categories = [
      { key: 'positionName', values: ['', ...annotationClassNames.positionName] },
      { key: 'highlight', values: ['', ...annotationClassNames.highlight] },
      { key: 'note', values: ['', ...annotationClassNames.note] },
      { key: 'placemark', values: ['', ...annotationClassNames.placemark] },
      { key: 'shape', values: ['', ...annotationClassNames.shape] },
    ];

    for (const combination of this.generateCombinations(categories)) {
      const tempBefore = this.getCssPseudoEachAnnotation(
        'before',
        combination.positionName,
        combination.highlight,
        combination.note,
        combination.placemark,
        combination.shape,
      );
      if (tempBefore) {
        beforeCssContent += tempBefore;
      }

      const tempAfter = this.getCssPseudoEachAnnotation(
        'after',
        combination.positionName,
        combination.highlight,
        combination.note,
        combination.placemark,
        combination.shape,
      );
      if (tempAfter) {
        afterCssContent += tempAfter;
      }
    }

    return [beforeCssContent, afterCssContent];
  }

  private * generateCombinations(
    categories: Array<{ key: string, values: string[] }>,
    index = 0,
    current: Record<string, string> = {},
  ): Generator<Record<string, string>> {
    if (index === categories.length) {
      yield { ...current };
      return;
    }

    const category = categories[index];
    for (const value of category.values) {
      yield * this.generateCombinations(categories, index + 1, { ...current, [category.key]: value });
    }
  }

  getCssPseudoEachAnnotation(
    pseudoType: string | null,
    positionName: string | null,
    colorHighlight: string | null,
    note: string | null,
    placemark: string | null,
    shape: string | null,
  ): string | undefined {
    if (pseudoType === 'after') {
      if (
        !positionName ||
        ![
          'annotation-end',
          'annotation-start-end',
          'annotation-start',
        ].includes(positionName)
      ) {
        return;
      }
    }

    if (pseudoType === 'before') {
      if (
        !positionName ||
        ![
          'annotation-start',
          'annotation-middle',
          'annotation-start-end',
        ].includes(positionName)
      ) {
        return;
      }
    }
    let selectors = ['.annotation'];
    let contentPieces: string[] = [];
    let cssString = '';
    let linker = this.locals['shared.with'];

    if (colorHighlight) {
      const [color, type] = colorHighlight.split('-');
      const colorHL = 'context.' + type + '.' + color;
      selectors = selectors.concat(['highlight', colorHighlight]);
      contentPieces.push(this.locals[colorHL]);
    }

    if (shape) {
      const shapeType = 'annotation.' + shape;
      selectors.push(shape);
      contentPieces.push(this.locals[shapeType]);
    }

    if (note) {
      selectors.push('note');
      if (contentPieces.length > 0) {
        contentPieces.push(linker);
        linker = this.locals['shared.and'];
      }
      contentPieces.push(this.locals['annotation.note']);
    }

    if (placemark) {
      selectors.push('placemark');
      if (contentPieces.length > 0) {
        contentPieces.push(linker);
      }
      contentPieces.push(this.locals['annotation.placemark']);
    }

    if (positionName) {
      selectors.push(positionName);
      if (
        pseudoType === 'before' &&
        ['annotation-start', 'annotation-start-end'].includes(positionName)
      ) {
        contentPieces.push(this.locals['annotation.begin']);
      }

      if (pseudoType === 'after' && positionName === 'annotation-start') {
        contentPieces = [];
      }

      if (
        pseudoType === 'after' &&
        ['annotation-end', 'annotation-start-end'].includes(positionName)
      ) {
        contentPieces.push(this.locals['annotation.end']);
      }
    }

    if (contentPieces.length > 0) {
      cssString = [
        selectors.join('.'),
        ':',
        pseudoType,
        '{content: "',
        contentPieces.join(' '),
        '";} ',
      ].join('');
    }

    return cssString;
  }

  getAnnotationBeforeCssFont(selectors): string {
    let cssString = '';

    if (Array.isArray(selectors) && selectors.length > 0) {
      const selectorString = selectors.join(':before,') + ':before';

      cssString = this.createMarkCssString(selectorString);
    }

    return cssString;
  }

  getAnnotationAfterCss(selectors): string {
    let cssString = '';
    let selectorString;

    if (Array.isArray(selectors) && selectors.length > 0) {
      selectorString = selectors.join(':after,') + ':after';
      cssString = this.createMarkCssString(selectorString);
    }

    return cssString;
  }

  getTeacherCss(): string {
    const teacherClasses = [
      'answer',
      'diff-resource',
      'example',
      'teach-notes',
    ];
    let cssString = '';

    teacherClasses.forEach((teacherClass) => {
      const translationKey = 'teaching_notes.' + teacherClass.replace('-', '_');
      const selector = '.teacher.' + teacherClass + ':before';
      const css = [
        selector,
        '{content: "',
        this.locals[translationKey],
        '";} ',
      ].join('');
      cssString += css;
    });

    return cssString;
  }

  createMarkCssString(selectorString: string): string {
    // See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/mark#accessibility_concerns for styling choice
    const clipPath = 'clip-path: inset(100%);';
    const clip = 'clip: rect(0.1px, 0.1px, 0.1px, 0.1px);';
    const height = 'height: 0.1px;';
    const overflow = 'overflow: hidden;';
    const position = 'position: absolute;';
    const whiteSpace = 'white-space: nowrap;';
    const width = 'width: 0.1px;';

    return [
      selectorString,
      '{',
      clipPath,
      clip,
      height,
      overflow,
      position,
      whiteSpace,
      width,
      '} ',
    ].join('');
  }
}
